import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { toastAnimations } from './sg-toast.animations';
import { SgToastAction, SgToastConfig, SgToastDismissEvent, SgToastType, TOAST_DURATION } from './sg-toast.model';

@Component({
  selector: 'sg-toast',
  templateUrl: './sg-toast.component.html',
  styleUrls: ['./sg-toast.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [toastAnimations.toastAnimation, toastAnimations.progressAnimation],
  host: {
    class: 'sg-toast',
    '[class.sg-toast--success]': 'type === "success"',
    '[class.sg-toast--info]': 'type === "info"',
    '[class.sg-toast--warning]': 'type === "warning"',
    '[class.sg-toast--error]': 'type === "error"',
    '[class.sg-toast--wait]': 'type === "wait"',
    '[class.sg-toast--with-action]': 'action',
    '[class.sg-toast--inline]': 'inline',
    '[class.sg-toast--inline-column]': 'inline && inlineFlexDirection === "column"',
    '[class.sg-toast--hide-icon]': 'hideIcon',
    '[class.sg-toast--with-title]': 'title',
    '[class.sg-toast--with-description]': 'description',
    '[class.sg-toast--summary]': 'data?.isSummaryToast',
    '[style.height]': 'height',
    '[class.sg-toast--with-line-breaks]': 'descriptionLineBreaks',
  },
  standalone: false,
})
export class SgToastComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  //#region Properties
  @Input() type: SgToastType = 'info';
  @Input() title?: string;
  @Input() description?: string;
  @Input() showCloseButton = true;
  @Input() action?: SgToastAction;
  @Input() inline = false;
  @Input() inlineFlexDirection: 'row' | 'column' = 'row';
  @Input() hideIcon = false;
  @Input() preventAutoClose = false;
  @Input() showProgressBar = false;
  @Input() disableAnimation: boolean = false;
  @Input() maxWidth?: string;
  @Input() descriptionTooltip?: string;
  @Input() height?: string;
  @Input() descriptionLineBreaks: boolean = false;

  @Output() dismissed = new EventEmitter<SgToastDismissEvent>();
  @Output() hideStackedEffectRequest = new EventEmitter<void>();

  animationState = 'visible';
  progressAnimationState = 'start';
  animationParams: { duration: number } = { duration: 0 };

  private timeoutId: any = null;
  private useSnackBar = false;
  private parentResizeObserver?: ResizeObserver;
  //#endregion

  //#region Ctor
  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_SNACK_BAR_DATA) public data: SgToastConfig,
    @Optional() public snackBarRef: MatSnackBarRef<SgToastComponent>
  ) {
    this.useSnackBar = !!this.snackBarRef && typeof this.snackBarRef.dismiss === 'function';

    this.setProps();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    this.updateProps();
    this.setupAutoClose();
  }

  ngOnChanges(): void {
    this.setupAutoClose();
  }

  ngAfterViewInit(): void {
    this.addAttributeToParentContainer();
    this.constrainInlineWidth();
    this.observeParentResize();
  }

  ngOnDestroy(): void {
    this.clearDismissTimeout();
    this.disconnectParentResize();
  }
  //#endregion

  //#region Public Methods
  dismiss(): void {
    if (this.animationState === 'hidden') {
      return;
    }

    this.clearDismissTimeout();
    this.animationState = 'hidden';

    const isSummaryToast = !!this.data?.isSummaryToast;

    setTimeout(() => {
      this.dismissed.emit({ isSummaryToast });

      if (this.useSnackBar && this.snackBarRef && typeof this.snackBarRef.dismiss === 'function') {
        this.snackBarRef.dismiss();
      }
    }, 200);
  }

  hideStackedEffect(): void {
    this.hideStackedEffectRequest.emit();
  }

  onActionClick(): void {
    const action = this.data?.action || this.action;
    if (!action) return;

    const shouldDismiss = !action.preventDismiss;

    if (action.callback) {
      action.callback();
    } else if (action.href) {
      window.location.href = action.href;
    }

    if (shouldDismiss) {
      this.dismiss();
    }
  }

  getIconName(): string {
    if (this.data?.isSummaryToast) {
      return 'check_circle';
    }
    switch (this.type) {
      case 'success':
        return 'check_circle';
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'wait':
        return ''; // No icon, will show spinner
      default:
        return 'info';
    }
  }

  updateFromData(): void {
    if (this.data) {
      this.type = this.data.type || this.type;
      this.title = this.data.title;
      this.description = this.data.description;
      this.action = this.data.action;
      this.showCloseButton = this.data.showCloseButton ?? this.showCloseButton;
      this.preventAutoClose = this.data.preventAutoClose ?? this.preventAutoClose;
      this.showProgressBar = this.data.showProgressBar !== false;

      // Reset auto-close timer if needed
      if (!this.preventAutoClose && this.type !== 'wait') {
        this.setupAutoClose();
      } else {
        this.clearDismissTimeout();
      }

      // Trigger animation for state change
      this.animationState = this.animationState === 'visible' ? 'changed' : 'visible';

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }
  //#endregion

  //#region Private Methods
  private setupAutoClose(): void {
    this.clearDismissTimeout();

    if (!this.showCloseButton || this.preventAutoClose || this.type === 'wait') {
      return;
    }

    const duration = this.action
      ? TOAST_DURATION.WITH_ACTION
      : this.type === 'success' || this.type === 'info'
        ? TOAST_DURATION.SUCCESS_INFO
        : TOAST_DURATION.WARNING_ERROR;

    if (duration > 0) {
      this.animationParams = { duration };
      this.cdr.detectChanges();

      requestAnimationFrame(() => {
        this.progressAnimationState = 'end';
        this.cdr.markForCheck();
      });

      this.timeoutId = window.setTimeout(() => {
        this.dismiss();
      }, duration);
    }
  }

  private clearDismissTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private addAttributeToParentContainer(): void {
    let parent = this.elementRef.nativeElement.parentElement;

    while (parent && !parent.classList.contains('mat-snack-bar-container')) {
      parent = parent.parentElement;
    }

    if (parent) {
      parent.setAttribute('sg-transparent-container', 'true');
      parent.style.background = 'transparent';
      parent.style.boxShadow = 'none';
      parent.style.padding = '0';

      setTimeout(() => {
        parent.style.backgroundColor = 'transparent';
        parent.style.background = 'transparent';
      });
    }
  }

  private setProps() {
    if (this.data) {
      this.type = this.type !== undefined ? this.type : (this.data.type ?? 'info');
      this.title = this.title !== undefined ? this.title : this.data.title;
      this.description = this.description !== undefined ? this.description : this.data.description;
      this.action = this.action !== undefined ? this.action : this.data.action;
      this.inline = this.inline !== undefined ? this.inline : !!this.data.inline;
      this.showProgressBar =
        this.showProgressBar !== undefined ? this.showProgressBar : this.data.showProgressBar !== false;
    }
  }

  private updateProps() {
    if (this.data) {
      if (this.inline) {
        this.showCloseButton =
          this.showCloseButton !== undefined ? this.showCloseButton : (this.data.showCloseButton ?? true);
        this.preventAutoClose =
          this.preventAutoClose !== undefined ? this.preventAutoClose : !!this.data.preventAutoClose;
      }
      this.showProgressBar =
        this.showProgressBar !== undefined ? this.showProgressBar : this.data.showProgressBar !== false;
    }
  }

  private constrainInlineWidth(): void {
    if (this.inline && this.maxWidth) {
      const parentElement = this.elementRef.nativeElement.parentElement;
      if (parentElement) {
        const parentWidth = parentElement.getBoundingClientRect().width;
        if (parentWidth > 0) {
          if (this.maxWidth === '100%') {
            // Use exact parent width, no minimum constraint
            const constrainedWidth = Math.floor(parentWidth);
            this.elementRef.nativeElement.style.maxWidth = `${constrainedWidth}px`;
            this.elementRef.nativeElement.style.width = `${constrainedWidth}px`;
          } else {
            // Use the provided maxWidth value
            this.elementRef.nativeElement.style.maxWidth = this.maxWidth;
            this.elementRef.nativeElement.style.width = '100%';
          }
          this.elementRef.nativeElement.style.boxSizing = 'border-box';
          this.elementRef.nativeElement.style.overflow = 'hidden';
        }
      }
    }
  }

  private observeParentResize(): void {
    if (!this.inline || !this.maxWidth || typeof ResizeObserver === 'undefined') {
      return;
    }

    const parentElement = this.elementRef.nativeElement.parentElement;
    if (!parentElement) {
      return;
    }

    // Re-measure whenever the parent's width changes — covers the initial
    this.parentResizeObserver = new ResizeObserver(() => this.constrainInlineWidth());
    this.parentResizeObserver.observe(parentElement);
  }

  private disconnectParentResize(): void {
    this.parentResizeObserver?.disconnect();
    this.parentResizeObserver = undefined;
  }
  //#endregion
}
